import { NoteObservableEditor } from "@/components/note/note-observable-editor";
import { TextDiff } from "@/components/note/diff/text-diff";
import {
  NOTE_EDITOR_TOP_BAR_HEIGHT,
  NoteEditorTopBar,
} from "@/components/note/editor/top-bar/note-editor-top-bar";
import { MemCommonEditorStore } from "@/components/note/store/mem-common-editor-store";
import { NoteEditorMode } from "@/components/note/types";
import { NoteSkeleton } from "@/components/note/editor/loading/note-skeleton";
import { MDSMediaQueries } from "@/mds/foundations";
import { css } from "@/domains/emotion";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import { UNTITLED_NOTE_TITLE } from "@/domains/untitled/untitled";
import { MDSButton, MDSButtonVariant } from "@/mds";
import { logger } from "@/modules/logger";
import { forceReloadWindow } from "@/modules/window/reload";
import { IContactModel } from "@/store/contacts/types";
import { NoteObservable } from "@/store/note/NoteObservable";
import { INoteObservable, NoteContentVersion } from "@/store/note/types";
import { INotesViewPageStore } from "@/store/pages/NotesViewPageStore/types";
import { UpdateNoteContentUsingDiffOperation } from "@/store/sync/operations/notes/UpdateNoteContentUsingDiffOperation";
import { useAppStore } from "@/store/utils/hooks";
import { useTabPageStore } from "@/components/layout/components/tab-bar/use-tab-page-store";
import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { HtmlPlaceholderPreset } from "@mem-labs/common-editor";
import { computed } from "mobx";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { AnimatePresence, motion, MotionProps } from "motion/react";
import useMeasure from "react-use/lib/useMeasure";
import { MDSAnimations } from "@/mds";

interface NoteEditorProps {
  isSidePanelOpen: boolean;
  goToMention?: INotesViewPageStore["goToMention"];
  openFileUploadRejectedModal?: INotesViewPageStore["openFileUploadRejectedModal"];
  openImageUploadRejectedModal?: INotesViewPageStore["openImageUploadRejectedModal"];
  findInNote?: INotesViewPageStore["findInNote"];
  noteObservable?: INoteObservable;
  myAccount?: IContactModel;
  inSidePanel?: boolean;
  highlightText?: string;
  autoFocus?: boolean;
  autoFocusPosition?: "start" | "end";
  versions?: NoteContentVersion[];
  onNoteRendered?: (noteId: string) => void;
}

export const NoteEditor = observer<NoteEditorProps>(function NoteEditor({
  goToMention,
  openFileUploadRejectedModal,
  openImageUploadRejectedModal,
  findInNote,
  noteObservable,
  myAccount,
  highlightText,
  autoFocus,
  autoFocusPosition,
  versions,
  onNoteRendered,
}) {
  const { store } = useAppStore();
  const page = useTabPageStore("notesViewPage");

  const [editorStore, setEditorStore] = useState<MemCommonEditorStore>();

  const [setContainerRef, { width: containerWidth }] = useMeasure<HTMLDivElement>();

  const theme = useTheme();
  const editorBackgroundStyles = css({
    background: `linear-gradient(${theme.colors.surface.pane} 30%, ${theme.colors.surface.pane}00), linear-gradient(${theme.colors.surface.pane}00, ${theme.colors.surface.pane} 70%) 0 100%, radial-gradient(farthest-side at 50% 0, rgba(0, 0, 0, .08), rgba(0, 0, 0, 0)), radial-gradient(farthest-side at 50% 100%, rgba(0, 0, 0, .08), rgba(0, 0, 0, 0)) 0 100%`,
    backgroundColor: theme.colors.surface.pane,
  });

  const showSkeleton =
    !noteObservable ||
    !noteObservable.isNoteContentDocumentLoaded ||
    !noteObservable.spaceAccountNote ||
    page.isLoading;

  const [showRefreshButton, setShowRefreshButton] = useState(false);
  const shouldShowProcessingIndicator =
    noteObservable instanceof NoteObservable && noteObservable.isProcessingSuggestion;

  const { titleText } = computed(() => {
    if (!noteObservable) return { titleText: "", title: "" };

    const suggestionData = noteObservable.suggestionData;
    if (suggestionData?.suggested_primary_label) {
      const oldText = noteObservable.title === UNTITLED_NOTE_TITLE ? "" : noteObservable.title;
      const titleText = suggestionData.suggested_primary_label;
      const title = <TextDiff oldText={oldText} newText={titleText} />;
      return { titleText, title };
    }

    for (const { operation } of noteObservable.memoryQueue?.operations.toReversed() ?? []) {
      if (operation.operationKind !== "UPDATE_NOTE_CONTENT_USING_DIFF") continue;

      const primaryLabel = (operation as UpdateNoteContentUsingDiffOperation).primaryLabel;
      if (primaryLabel) return { titleText: primaryLabel, title: primaryLabel };
    }
    const titleText = noteObservable.title || UNTITLED_NOTE_TITLE;
    return { titleText, title: titleText };
  }).get();

  useTitle(titleText);
  useLockNote(noteObservable);

  // Clean up page-scoped note version history when component unmounts
  useEffect(() => {
    return () => {
      page.noteVersionHistory.closeNoteVersionHistory();
    };
  }, [page.noteVersionHistory]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (showSkeleton) {
      timeoutId = setTimeout(() => {
        logger.warn({
          message: "[NoteEditor][showRefreshButton] Time elapsed, showing refresh button",
          info: {
            noteId: noteObservable?.id || "",
            noteTitle: noteObservable?.title || "",
          },
        });

        setShowRefreshButton(true);
      }, 5000);
    } else {
      setShowRefreshButton(false);
    }
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSkeleton]);

  useEffect(() => {
    if (!noteObservable) return;
    store.notes.setNewNoteIsBeingCreated(false);
  }, [noteObservable, store]);

  /**
   * Track views of the note editor.
   */
  useEffect(() => {
    // Wait for note and flag to be loaded
    if (!noteObservable) return;

    trackEvent(TrackedEvent.NoteView, {
      note_id: noteObservable.id,
      note_primary_label: noteObservable.title,
    });
    // 1. We explicitly don't want to re-run this effect when the note observable changes, only id
    // Thats because when new note is created, observable can get refreshed,
    // resulting in duplicate events
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteObservable?.id]);

  const diffModeStyles = css({
    background: "unset",
    backgroundColor: theme.colors.background.diff,
    width: "100%",
  });

  const editorModeClassName = editorStore?.isShowingDifferences ? diffModeStyles : "";

  /**
   * If we've finished loading (there is no skeleton), and the note observable is not loaded,
   * we want to log a warning.
   */
  if (!showSkeleton) {
    if (!noteObservable || !noteObservable.spaceAccountNote) {
      logger.info({
        message: "[NoteEditor] NoteObservable or spaceAccountNote is undefined",
        info: {
          hasNoteObservable: !!noteObservable,
          hasSpaceAccountNote: !!noteObservable?.spaceAccountNote,
        },
      });
    }
  }

  return (
    <AnimatePresence mode="wait">
      {showSkeleton ? (
        <motion.div key="note-skeleton" {...skeletonAnimationProps}>
          <NoteSkeleton />
          {/** If we are loading for an unexpectedly long time, we show a refresh button */}
          {showRefreshButton && (
            <RefreshButtonWrapper>
              <MDSButton variant={MDSButtonVariant.Contrast} onClick={() => forceReloadWindow()}>
                This note is taking longer than expected to load. Refresh?
              </MDSButton>
            </RefreshButtonWrapper>
          )}
        </motion.div>
      ) : (
        <motion.div
          key="note-content"
          className={fullHeight}
          ref={setContainerRef}
          {...contentAnimationProps}
        >
          <ContentWrapper className={editorModeClassName}>
            <NoteEditorTopBar
              editorStore={editorStore}
              note={noteObservable}
              containerWidth={containerWidth}
            />
            <NoteObservableEditor
              editorModeClassName={editorModeClassName}
              editorStore={editorStore}
              setEditorStore={setEditorStore}
              showMetadata={false}
              containerWidth={containerWidth}
              autoFocus={autoFocus}
              autoFocusPosition={autoFocusPosition}
              className={css(editorStyles, editorBackgroundStyles)}
              goToMention={goToMention}
              openFileUploadRejectedModal={openFileUploadRejectedModal}
              openImageUploadRejectedModal={openImageUploadRejectedModal}
              findInNote={findInNote}
              noteObservable={noteObservable}
              myAccount={myAccount}
              mode={NoteEditorMode.Editable}
              highlightText={highlightText}
              versions={versions}
              onNoteRendered={onNoteRendered}
              htmlPlaceholderPreset={HtmlPlaceholderPreset.Default}
            />
            {shouldShowProcessingIndicator && (
              <PulsingOverlay hasSources={noteObservable.hasSources} />
            )}
          </ContentWrapper>
        </motion.div>
      )}
    </AnimatePresence>
  );
});

const fullHeight = css({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  position: "relative",
  width: "100%",
});

const ContentWrapper = styled.div({
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
  minHeight: 0,
});

// Note: left padding is always 16px larger than right padding due to the scrollbar.
// Only issue is on mobile where the scrollbar is overlay it's not part of the width calculations.
const editorStyles = css({
  backgroundRepeat: "no-repeat",
  backgroundSize: "100% 30px, 100% 30px, 100% 12px, 100% 12px",
  backgroundAttachment: "local, local, scroll, scroll",
  boxSizing: "border-box",
  cursor: "text",
  display: "flex",
  flexDirection: "column",
  height: `calc(100% - ${NOTE_EDITOR_TOP_BAR_HEIGHT})`,
  overflowY: "auto",
  overflowX: "hidden",
  position: "relative",
  // Consistent sizing whether the scrollbar is visible or not.
  scrollbarGutter: "stable",

  "--extra-padding-left": `28px`,
  "--extra-padding-right": `28px`,
  "--extra-padding-bottom": "200px",

  [MDSMediaQueries.rules.tablet]: {
    "--extra-padding-left": `28px`,
    "--extra-padding-right": `28px`,
    "--extra-padding-bottom": "100px",
  },

  [MDSMediaQueries.rules.phone]: {
    "--extra-padding-left": `16px`,
    "--extra-padding-right": `16px`,
    "--extra-padding-bottom": "100px",
  },
});

const PulsingOverlay = styled.div<{ hasSources: boolean }>(({ hasSources }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: hasSources ? "64px" : 0,
  pointerEvents: "none",
  overflow: "hidden",

  "&::before": {
    content: '""',
    position: "absolute",
    top: "50%",
    left: "50%",
    width: "100%",
    height: "100%",
    transform: "translate(-50%, -50%)",
    animation: "pulse 2s ease-in-out infinite",
    background:
      "radial-gradient(circle at center, rgba(60, 111, 246, 0.1) 0%, rgba(60, 111, 246, 0) 150%)",
    opacity: 0,
  },

  "@keyframes pulse": {
    "0%": {
      opacity: 0,
      transform: "translate(-50%, -50%) scale(1)",
    },
    "50%": {
      opacity: 1,
      transform: "translate(-50%, -50%) scale(1.2)",
    },
    "100%": {
      opacity: 0,
      transform: "translate(-50%, -50%) scale(1)",
    },
  },
}));

/** Preserve the default document title */
const defaultTitle = document.title;

/** Prefix document title with Note title */
const useTitle = (title: string): void => {
  document.title = title ? `${title} - Mem` : defaultTitle;
  useEffect(() => {
    return () => {
      document.title = defaultTitle;
    };
  }, []);
};

const useLockNote = (note: INoteObservable | undefined): void => {
  useEffect(() => {
    note?.lock();
    return () => {
      note?.unlock();
    };
  }, [note]);
};

const RefreshButtonWrapper = styled.div({
  display: "flex",
  justifyContent: "center",
});

/**
 * We have a 2-second delay, to give the app a chance to load the
 * content before hitting them with a skeleton.
 */
const skeletonAnimationProps = {
  initial: "hidden",
  animate: "visible",
  exit: "exit",
  variants: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { delay: 2, ...MDSAnimations.fadeIn },
    },
    exit: { opacity: 0, transition: { ...MDSAnimations.fadeOut } },
  },
} satisfies MotionProps;

const contentAnimationProps = {
  initial: "hidden",
  animate: "visible",
  exit: "exit",
  variants: {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { ...MDSAnimations.fadeIn } },
    exit: { opacity: 0, transition: { ...MDSAnimations.fadeOut } },
  },
} satisfies MotionProps;
